import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import useTranslator from '../../../../hooks/useTranslator';
import useConfig from '../../../../hooks/useConfig';
import useFeatureToggle from '../../../../hooks/useFeatureToggle';
import StateInterface from '../../../../redux/types';
import { CategoryData } from '../../../../redux/category/types';
import { getCategoriesUrl } from '../../../../urlService';
import Menu from '../../../Overlays/Menu';
import { MenuItemType, MenuItemInterface, MenuSectionInterface } from '../../../Overlays/Menu/types';
import { MagalogState } from '../../../../../common/types/remoteConfig';
import useGa from '../../../../hooks/useGa';
import { useIsLoggedIn } from '../../../../hooks/useIsLoggedIn';
import { VictorQaClasses } from '../../../../../common/qaIds';
import { createMenuItems } from '../../../Overlays/NewAccountFlyout';
import initialMenuItems, { ACCOUNT_SECTION_KEY } from './initialMenuItem';
import useTheme from 'Client/hooks/useTheme';

export const MAX_CATEGORY_DEPTH_LEVEL = 2;

export const BurgerContainer: FC<{
    navDown: (newTitle: string, newMenuItems: MenuSectionInterface[]) => void;
    menuItems: MenuSectionInterface[][];
    closeMenuFn: () => void;
    strict?: boolean;
    bordered?: boolean;
    search?: boolean;
}> = ({ navDown, menuItems, closeMenuFn, strict, bordered, search }) => {
    const t = useTranslator();
    const config = useConfig();
    const ga = useGa();
    const theme = useTheme();
    let { interiorServiceInNav } = useFeatureToggle();
    const isUserLoggedIn = useIsLoggedIn();
    interiorServiceInNav = interiorServiceInNav || false;
    const magalogConfig = useSelector<StateInterface, MagalogState>(state => state.configurations.magalog);

    const categoriesToBurgerItems = (cats: CategoryData[]): MenuItemInterface[] =>
        cats.map((category: CategoryData) => ({
            ...category,
            url: getCategoriesUrl(category.url),
            itemType:
                category.children && category.children.length > 0 && category.depthLevel < MAX_CATEGORY_DEPTH_LEVEL
                    ? MenuItemType.Button
                    : MenuItemType.InternalLink,
            title: category.name,
            hexColor: category.hexColor,
            depthLevel: category.depthLevel,
            navDownCallback: () => {
                if (category.children) {
                    navDown(category.name, [
                        {
                            key: category.name,
                            items: [
                                {
                                    title: category.allProductsText || t('All articles'),
                                    url: getCategoriesUrl(category.url),
                                    itemType: MenuItemType.InternalLink,
                                    hexColor: category.hexColor,
                                },
                                ...categoriesToBurgerItems(category.children),
                            ],
                        },
                    ]);
                }
            },
            trackItemClick: () => {
                const { name, depthLevel } = category;
                if (search && depthLevel === 0) {
                    ga.oneHeader.trackSearchClicks(name);
                    return;
                }
                ga.oneHeader.trackBurgerItemClick(name);
            },
        }));

    const buildAccountMenuItemData = (): MenuSectionInterface => {
        const accountTitle = t('My account');
        return {
            key: ACCOUNT_SECTION_KEY,
            items: [
                {
                    title: accountTitle,
                    url: config.links.myAccount,
                    itemType: MenuItemType.Button,
                    hexColor: theme.colors.white,
                    testId: 'myaccount-btn',
                    className: isUserLoggedIn
                        ? VictorQaClasses.headerAccountLink
                        : VictorQaClasses.headerBurgerMenuLoginItem,
                    navDownCallback: () => {
                        navDown(accountTitle, [
                            {
                                key: accountTitle,
                                items: [...createMenuItems(t, config)],
                            },
                        ]);
                    },
                },
            ],
        };
    };

    useEffect(() => {
        if (!isDesktop()) {
            ga.oneHeader.trackBurgerMenuImpression(menuItems.length + 1);
        }
    }, [ga.oneHeader, menuItems]);

    const visibleCategoryData: CategoryData[] = useSelector<StateInterface, CategoryData[]>(
        state => state.categories.visibleOriginalCategories
    );

    const burgerMenuItemData = initialMenuItems(
        categoriesToBurgerItems(visibleCategoryData),
        { t, ga },
        {
            search,
            strict,
            config,
            interiorServiceInNav,
            isUserLoggedIn,
            magaLogConfig: magalogConfig,
        }
    );
    if (!strict) {
        burgerMenuItemData.push(buildAccountMenuItemData());
    }

    return (
        <Menu
            className={VictorQaClasses.headerBurgerMenuContainer}
            menuSections={menuItems.length === 0 ? burgerMenuItemData : menuItems[menuItems.length - 1]}
            closeMenuFn={closeMenuFn}
            bordered={bordered}
            search={search}
        />
    );
};

export default BurgerContainer;
